import { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { subscribeNotificationInsert } from "@/lib/notificationsBus";
import { useAuth } from "@/contexts/AuthContext";
import { AlertCard, DispatcherAlert } from "./AlertCard";
import { Inbox, Trash2, CheckCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const ALL_KEY = "__all__";

interface Props {
  watchFilter?: string | null;
  onWatchFilterChange?: (id: string | null) => void;
}

export function DispatcherInbox({ watchFilter = null, onWatchFilterChange }: Props) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [alerts, setAlerts] = useState<DispatcherAlert[]>([]);
  const [loadDetails, setLoadDetails] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const { data } = await supabase
      .from("notifications")
      .select("id, load_id, title, message, metadata, is_read, created_at, action_url")
      .eq("recipient_user_id", userId)
      .eq("type", "dispatcher_match")
      .order("created_at", { ascending: false })
      .limit(50);
    const rows = (data as DispatcherAlert[]) || [];
    setAlerts(rows);

    const ids = Array.from(new Set(rows.map((r) => r.load_id).filter(Boolean) as string[]));
    if (ids.length) {
      const { data: loads } = await supabase
        .from("loads")
        .select("id, contact_phone, contact_email, rate_amount, total_miles, equipment_type, origin_city, origin_state, destination_city, destination_state")
        .in("id", ids);
      const map: Record<string, any> = {};
      (loads || []).forEach((l: any) => { map[l.id] = l; });
      setLoadDetails(map);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
    if (!userId) return;
    const off = subscribeNotificationInsert(userId, (row) => {
      if ((row as { type?: string })?.type === "dispatcher_match") load();
    });
    return off;
  }, [userId, load]);

  // Build pill list from alerts: group by metadata.watch_id with latest watch_label
  const pills = useMemo(() => {
    const map = new Map<string, { id: string; label: string; total: number; unread: number }>();
    for (const a of alerts) {
      const wid: string | undefined = a.metadata?.watch_id;
      const label: string = a.metadata?.watch_label || "Untitled watch";
      if (!wid) continue;
      const existing = map.get(wid);
      if (existing) {
        existing.total += 1;
        if (!a.is_read) existing.unread += 1;
        // keep most recent label (alerts already sorted desc)
      } else {
        map.set(wid, { id: wid, label, total: 1, unread: a.is_read ? 0 : 1 });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.unread - a.unread || b.total - a.total);
  }, [alerts]);

  const activeKey = watchFilter ?? ALL_KEY;
  const activePill = pills.find((p) => p.id === watchFilter) || null;
  const activeLabel = activePill?.label ?? "all lanes";

  const visibleAlerts = useMemo(() => {
    if (!watchFilter) return alerts;
    return alerts.filter((a) => a.metadata?.watch_id === watchFilter);
  }, [alerts, watchFilter]);

  // Clear selection when filter changes
  useEffect(() => { setSelected(new Set()); }, [watchFilter]);

  const handleSelectChange = useCallback((id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  const allSelected = useMemo(
    () => visibleAlerts.length > 0 && visibleAlerts.every((a) => selected.has(a.id)),
    [visibleAlerts, selected]
  );

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visibleAlerts.map((a) => a.id)));
  };

  const handleDeleted = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
    setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
  }, []);

  const handleToggleRead = useCallback((id: string, isRead: boolean) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, is_read: isRead } : a)));
  }, []);

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkBusy(true);
    const { error } = await supabase.from("notifications").delete().in("id", ids);
    setBulkBusy(false);
    if (error) {
      toast.error("Failed to delete alerts");
      return;
    }
    setAlerts((prev) => prev.filter((a) => !selected.has(a.id)));
    setSelected(new Set());
    setBulkConfirmOpen(false);
    toast.success(`Deleted ${ids.length} alert${ids.length === 1 ? "" : "s"}`);
  };

  const markAllRead = async () => {
    const scope = selected.size > 0 ? visibleAlerts.filter((a) => selected.has(a.id)) : visibleAlerts.filter((a) => !a.is_read);
    const ids = scope.map((a) => a.id);
    if (!ids.length) return;
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true, read_at: new Date().toISOString() })
      .in("id", ids);
    if (error) {
      toast.error("Failed to mark as read");
      return;
    }
    setAlerts((prev) => prev.map((a) => (ids.includes(a.id) ? { ...a, is_read: true } : a)));
    toast.success(`Marked ${ids.length} as read`);
  };

  const setActivePill = (id: string | null) => {
    onWatchFilterChange?.(id);
  };

  if (loading) return <div className="text-sm text-muted-foreground py-8 text-center">Loading inbox…</div>;
  if (!alerts.length) {
    return (
      <div className="py-16 text-center border border-dashed rounded-lg">
        <Inbox className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">No alerts yet. Create a watch and ATLAS will start surfacing loads here.</p>
      </div>
    );
  }

  const totalUnread = alerts.filter((a) => !a.is_read).length;

  const PillButton = ({
    active, onClick, label, count, unread,
  }: { active: boolean; onClick: () => void; label: string; count: number; unread: number }) => (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active
          ? "bg-brand-teal text-white border-brand-teal"
          : "bg-card text-foreground border-border hover:bg-muted"
      )}
    >
      {unread > 0 && (
        <span className={cn("h-1.5 w-1.5 rounded-full", active ? "bg-white" : "bg-brand-teal")} />
      )}
      <span className="truncate max-w-[140px]">{label}</span>
      <span className={cn("text-[10px] opacity-80", active ? "" : "text-muted-foreground")}>
        {unread > 0 ? `${unread}/${count}` : count}
      </span>
    </button>
  );

  return (
    <div className="space-y-3">
      {/* Pill strip */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
        <PillButton
          active={activeKey === ALL_KEY}
          onClick={() => setActivePill(null)}
          label="All"
          count={alerts.length}
          unread={totalUnread}
        />
        {pills.map((p) => (
          <PillButton
            key={p.id}
            active={activeKey === p.id}
            onClick={() => setActivePill(p.id)}
            label={p.label}
            count={p.total}
            unread={p.unread}
          />
        ))}
      </div>

      {/* Bulk toolbar */}
      <div className="flex items-center gap-3 px-1">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={allSelected}
            onCheckedChange={toggleSelectAll}
            aria-label="Select all"
            disabled={visibleAlerts.length === 0}
          />
          <span className="text-xs text-muted-foreground">
            {selected.size > 0
              ? `${selected.size} selected`
              : `${visibleAlerts.length} alert${visibleAlerts.length === 1 ? "" : "s"}${activePill ? ` · ${activePill.label}` : ""}`}
          </span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            onClick={markAllRead}
            disabled={visibleAlerts.length === 0}
          >
            <CheckCheck className="h-3.5 w-3.5 mr-1" />
            {selected.size > 0 ? "Mark selected read" : "Mark all read"}
          </Button>
          {selected.size > 0 && (
            <Button
              size="sm"
              variant="destructive"
              className="h-7 px-2 text-xs"
              onClick={() => setBulkConfirmOpen(true)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Delete selected ({selected.size})
            </Button>
          )}
        </div>
      </div>

      {visibleAlerts.length === 0 ? (
        <div className="py-16 text-center border border-dashed rounded-lg">
          <Inbox className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No alerts in this lane yet.</p>
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {visibleAlerts.map((a) => (
            <AlertCard
              key={a.id}
              alert={a}
              loadDetails={a.load_id ? loadDetails[a.load_id] : undefined}
              selected={selected.has(a.id)}
              onSelectChange={handleSelectChange}
              onDeleted={handleDeleted}
              onToggleRead={handleToggleRead}
            />
          ))}
        </div>
      )}

      <AlertDialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selected.size} alert{selected.size === 1 ? "" : "s"}
              {activePill ? ` from ${activePill.label}` : ""}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the selected alerts from {activeLabel}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); bulkDelete(); }}
              disabled={bulkBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
